import { useEffect, useMemo, useState } from 'react';
import type { CategoryListItem } from '../../lib/panelApi';
import { listCategories } from '../../lib/panelApi';

export function CategoriesPage({ accessToken }: { accessToken: string }) {
  const [items, setItems] = useState<CategoryListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listCategories(accessToken, search)
      .then((res) => {
        if (!cancelled) {
          setItems(res.items);
          setTotal(res.total);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load categories');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, search]);

  const totalBooks = useMemo(() => items.reduce((s, c) => s + c.book_count, 0), [items]);

  return (
    <div className="card">
      <div className="pageHeader" style={{ marginBottom: 10 }}>
        <div>
          <div className="pageTitle">Categories</div>
          <div className="pageSubtitle">
            All categories are auto-created from book metadata.
          </div>
        </div>
        <div className="row">
          <span className="pill">
            <span className="muted">Categories</span>
            <span>{total}</span>
          </span>
          <span className="pill">
            <span className="muted">Book assignments</span>
            <span>{totalBooks}</span>
          </span>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="toolbarGroup" style={{ flex: 1 }}>
          <input
            className="searchInput"
            type="text"
            placeholder="Search categories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        </div>
      </div>

      {error ? (
        <div className="errorBox" style={{ marginBottom: 12 }}>{error}</div>
      ) : null}

      {loading ? (
        <div className="muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="muted">No categories found.</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Name</th>
              <th style={{ textAlign: 'center', width: 120 }}>Books</th>
              <th style={{ textAlign: 'right', width: 180 }}>Created</th>
            </tr>
          </thead>
          <tbody>
            {items.map((cat) => (
              <tr key={cat.uuid}>
                <td>
                  <span style={{ fontWeight: 700 }}>{cat.name}</span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span className="pill">
                    <span>{cat.book_count}</span>
                  </span>
                </td>
                <td style={{ textAlign: 'right' }} className="muted">
                  {new Date(cat.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
